import React, { FC, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Modal from '../../../components/common/Modal'
import { buildWorkTime, format, secToHour, secToHourMinute } from '../../../../utils/date'
import Holidays from 'date-holidays'
import { clone } from 'lodash'
import { Compare } from '../../../../states/types/report'
import { useSelector } from 'react-redux'
import { RootState } from '../../../../states/containers/store'
import { setPath } from '../../../../utils/url'
import { useParams } from 'react-router-dom'
import { URL } from '../../../constants/url'
import Loading from '../../common/Loading'

interface Heatmap {
  display: boolean
  startDate: string
  endDate: string
  type: string
  target: string
  id: number
  title: string
  legend: string
  startColor: string
  endColor: string
  minimum: number
  displaySearchLink: boolean
  name: string
}

interface TipDetail {
  display: boolean
  type: string
  title: string
  date: string
  value: number
  link: string
}

interface Search {
  startDate: string
  endDate: string
  teamIds?: number[]
  teamNames?: string[]
  memberIds?: number[]
  memberNames?: string[]
  keywords?: string[]
  serviceNames?: string[]
  categoryIds?: number[]
  categoryNames?: string[]
  efficiencyLevel?: string[]
  stressLevel?: string[]
  focusLevel?: string[]
  sortKey?: string,
  sortType?: string,
}

interface Props {
  type: string
  target: string
  heatmap: Heatmap
  closeCallback: () => void
  searchModalOpenCallback: (search: Search) => void
}

type LangValue = 'ja' | 'en'

const HeatmapModal: FC<Props> = (props) => {
  const {
    type,
    target,
    heatmap,
    closeCallback,
    searchModalOpenCallback,
  } = props

  const {
    report,
    commonPart,
  } = useSelector((state: RootState) => (state))

  const { language } = useParams<{ language: LangValue }>()
  const { t } = useTranslation()

  const tipRef = useRef() as React.MutableRefObject<HTMLInputElement>
  const graphRef = useRef() as React.MutableRefObject<HTMLInputElement>

  const tipInit:TipDetail = {
    display: false,
    type: "",
    title: "",
    date: "",
    value: 0,
    link: "",
  }
  const [tipDetail, setTipDetail] = useState<TipDetail>(tipInit)

  const [fixTip, setFixTip] = useState<boolean>(false)

  const holidays = new Holidays()
  holidays.init('JP')

  const rangeList = useMemo(() => {
    const dateList = []
    for (let d = clone(new Date(heatmap.startDate));d <= new Date(heatmap.endDate);d.setDate(d.getDate() + 1)) {
      dateList.push(format(d, "YYYY-MM-DD"))
    }
    return Array.from(new Set(dateList))
  }, [heatmap.startDate, heatmap.endDate])

  const maxValue = useMemo(() => {
    if (!report.compare) return 1
    const maxList = report.compare.data.map((list) => (
      Math.max(...list.values.map((value) => value.value))
    ))
    return Math.max(...maxList)
  }, [report.compare])

  const modalTitle = useMemo(() => {
    if (!heatmap.type) return ""
    if (heatmap.title === "service") {
      return heatmap.name
    }
    if (heatmap.title === "judgement") {
      return t(`report.indicator.${heatmap.title}.${heatmap.name}`)
    }
    return t(`report.indicator.${heatmap.title}`)
  }, [heatmap.title, heatmap.name])

  const buildColor = (value: number) => {
    const adjust_value = value - heatmap.minimum > 0 ? value - heatmap.minimum : 0
    const percent = adjust_value / maxValue
    const sr = parseInt(heatmap.startColor.substring(1, 3), 16)
    const sg = parseInt(heatmap.startColor.substring(3, 5), 16)
    const sb = parseInt(heatmap.startColor.substring(5, 7), 16)
    const er = parseInt(heatmap.endColor.substring(1, 3), 16)
    const eg = parseInt(heatmap.endColor.substring(3, 5), 16)
    const eb = parseInt(heatmap.endColor.substring(5, 7), 16)

    const tr = (`00${(((er - sr) * percent) + sr).toString(16).split(".")[0]}`).slice(-2)
    const tg = (`00${(((eg - sg) * percent) + sg).toString(16).split(".")[0]}`).slice(-2)
    const tb = (`00${(((eb - sb) * percent) + sb).toString(16).split(".")[0]}`).slice(-2)

    return `#${tr}${tg}${tb}`
  }

  const calendarColor = (date: string) => {
    if (holidays.isHoliday(new Date(date)) || format(date, 'd') === '0') {
      return "#FF0000"
    }
    if (format(date, 'd') === '6') {
      return "#317AB7"
    }
    return "#666666"
  }

  const displayTip = (event: any, row_type: string, title: string, date: string, value: number, id: number, isButton: boolean) => {
    if (!tipRef.current) return
    if (fixTip) return
    const clientRect = graphRef.current.getBoundingClientRect()
    const x = event.pageX - (clientRect.left + window.pageXOffset)
    const y = event.pageY - (clientRect.top + window.pageYOffset)
    if (isButton) {
      tipRef.current.style.top = `${y - 40}px`
    } else {
      tipRef.current.style.top = `${y - 20}px`
    }
    tipRef.current.style.left = `${x + 15}px`
    setTipDetail({
      ...tipDetail,
      display: true,
      type: row_type,
      title: title,
      date: date,
      value: value,
      link: `${id}`,
    })
    setFixTip(!fixTip)
  }

  const closeTip = () => {
    if (!tipRef.current) return
    tipRef.current.style.top = "0px"
    tipRef.current.style.left = "0px"
    setTipDetail({
      ...tipDetail,
      display: false,
    })
    setFixTip(!fixTip)
  }

  const closeModal = () => {
    closeCallback()
    closeTip()
    setFixTip(false)
  }

  const qaseeAve = useMemo(() => report?.compare?.data.find((data) => data.type === "qasee_ave"), [report])
  const occupationAve = useMemo(() => report?.compare?.data.find((data) => data.type === "occupation_ave"), [report])
  const companyAve = useMemo(() => report?.compare?.data.find((data) => data.type === "company_ave"), [report])
  const teamAve = useMemo(() => report?.compare?.data.find((data) => data.type === "team_ave"), [report])
  const memberList = useMemo(() => report?.compare?.data.filter((data) => data.type === "member"), [report])

  const buildTitle = (value: Compare) => {
    if (value.type === "qasee_ave") {
      return "Qasee平均"
    }
    if (value.type === "occupation_ave") {
      return "職種平均"
    }
    if (value.type === "company_ave") {
      return "会社平均"
    }
    if (value.type === "team_ave") {
      return "チーム平均"
    }
    if (value.type === "member") {
      return commonPart.members?.find((member) => member.id === value.id)?.name || ""
    }
    return ""
  }

  const buildRow = (value: Compare, isButton = true) => {
    const title = buildTitle(value)
    return (
      <div
        className="row"
        key={`heatmap_${value.type}_${value.id}`}
      >
        <div
          className="name no_trans"
          style={{
            color: value.type !== "member" ? "#094E8D" : "#666",
          }}
        >
          {title}
        </div>
        <div
          className="average"
          style={{
            backgroundColor: buildColor(value.summary || 0),
          }}
        >
          {
            heatmap.type === "hour" || heatmap.type === "minute" ? (
              <>
                {secToHourMinute(value.summary || 0)}
              </>
            ) : (
              <>
                {value.summary && value.summary.toLocaleString()}
                {t(`report.detail.unit.${heatmap.type}`)}
              </>
            )
          }
        </div>
        {
          rangeList.map((date) => (
            <React.Fragment
              key={`heatmap_ch_${value.type}_${title}_${date}`}
            >
              {
                value.values.find((v) => v.date === format(date, "YYYY-MM-DD"))?.value !== null ? (
                  <div
                    className="item"
                    style={{
                      backgroundColor: buildColor(value.values.find((v) => v.date === format(date, "YYYY-MM-DD"))?.value || 0),
                    }}
                    role="none"
                    onClick={
                      (event) => displayTip(
                        event,
                        value.type,
                        title,
                        format(date, "YYYY-MM-DD"),
                        value.values.find((v) => v.date === format(date, "YYYY-MM-DD"))?.value || 0,
                        value.id,
                        isButton,
                      )
                    }
                  />
                ) : (
                  <div
                    className="item no_data"
                    style={{
                      cursor: "default",
                    }}
                  />
                )
              }
            </React.Fragment>
          ))
        }
      </div>
    )
  }

  const clickSearch = () => {
    closeModal()
    const requestParams: Search = {
      startDate: tipDetail.date,
      endDate: tipDetail.date,
      memberIds: [Number(tipDetail.link)],
      memberNames: [tipDetail.title],
    }
    if (heatmap.title === "service") {
      requestParams.serviceNames?.push(heatmap.name)
    }
    if (heatmap.title === "judgement") {
      const name_array = heatmap.name.split('_')
      if (name_array[0] === "efficiency") {
        requestParams.efficiencyLevel = [name_array[1]]
        requestParams.sortKey = "efficiency"
      }
      if (name_array[0] === "stress") {
        requestParams.stressLevel = [(name_array[1])]
        requestParams.sortKey = "stress"
      }
      if (name_array[0] === "focus") {
        requestParams.focusLevel = [(name_array[1])]
        requestParams.sortKey = "focus"
      }
      if (name_array[1] === "a") {
        requestParams.sortType = "desc"
      } else {
        requestParams.sortType = "asc"
      }
      requestParams.categoryIds = [1, 3, 4, 5, 6, 7]
      requestParams.categoryNames = ["文章", "クリエイティブ", "インターネット", "経理", "マーケティング", "その他"]
    }
    if (heatmap.title === "communication_app_use_time") {
      requestParams.categoryIds = [2]
      requestParams.categoryNames = ["コミュニケーション"]
    }
    if (heatmap.title === "biz_point") {
      requestParams.sortKey = "biz"
      requestParams.sortType = "desc"
    }
    searchModalOpenCallback(requestParams)
  }

  return (
    <Modal
      title="比較データ"
      show={heatmap.display}
      wrapperClass={`report_modal ${type} heatmap_wrap`}
      closeCallback={closeModal}
      closeable
    >
      <>
        <div className="title">
          {modalTitle}
        </div>
        {
          report.isLoadingCompare ? (
            <Loading />
          ) : (
            <div className="heatmap_graph">
              <div className="range">
                {format(heatmap.startDate, "YYYY年MM月DD日")}
                ~
                {format(heatmap.endDate, "DD日")}
              </div>
              <div
                className="heatmap"
                ref={graphRef}
              >
                {
                  qaseeAve && (
                    buildRow(qaseeAve, false)
                  )
                }
                {
                  occupationAve && (
                    buildRow(occupationAve, false)
                  )
                }
                {
                  companyAve && (
                    buildRow(companyAve, false)
                  )
                }
                {
                  teamAve && (
                    buildRow(teamAve, false)
                  )
                }
                {
                  memberList && memberList.map((member) => (
                    buildRow(member, heatmap.displaySearchLink)
                  ))
                }
                <div className="label">
                  <div className="name" />
                  <div className="average">
                    1日平均
                  </div>
                  {
                    rangeList.map((date) => (
                      <div
                        className="item"
                        style={{
                          color: calendarColor(date),
                        }}
                        key={`heatmap_label_${date}`}
                      >
                        {format(date, "DD")}
                      </div>
                    ))
                  }
                </div>
                <div
                  className="close_heatmap_screen"
                  style={{
                    display: fixTip ? "block" : "none",
                  }}
                  role="none"
                  onClick={() => closeTip()}
                />
              </div>
              <div className="bottom_area">
                <div className="legend">
                  <div className="legend_title">
                    {heatmap.legend}
                  </div>
                  <div
                    className="bar"
                    style={{
                      background: `linear-gradient(to right, ${heatmap.startColor}, ${heatmap.endColor})`,
                    }}
                  />
                  <div className="label">
                    {
                      heatmap.type === "hour" || heatmap.type === "minute" ? (
                        <>
                          <div>
                            {secToHour(heatmap.minimum || 0)}
                            h
                          </div>
                          <div>
                            {secToHour(maxValue)}
                            h
                          </div>
                        </>
                      ) : (
                        <>
                          <div>
                            {(heatmap.minimum || 0).toLocaleString()}
                            {t(`report.detail.unit.${heatmap.type}`)}
                          </div>
                          <div>
                            {maxValue.toLocaleString()}
                            {t(`report.detail.unit.${heatmap.type}`)}
                          </div>
                        </>
                      )
                    }
                  </div>
                </div>
              </div>
              <div
                ref={tipRef}
                className="tip_wrap"
                style={{
                  display: tipDetail.display ? "block" : "none",
                }}
              >
                <div className="tip">
                  <div className="title no_trans">
                    {tipDetail.title}
                  </div>
                  <div className="date">
                    {tipDetail.date}
                  </div>
                  <div className="value">
                    {
                      heatmap.type === "hour" || heatmap.type === "minute" ? (
                        <>
                          {buildWorkTime(tipDetail.value)}
                        </>
                      ) : (
                        <>
                          {tipDetail.value.toLocaleString()}
                          {t(`report.detail.unit.${heatmap.type}`)}
                        </>
                      )
                    }
                  </div>
                  {
                    heatmap.displaySearchLink && tipDetail.link && tipDetail.type === "member" && (
                      <button
                        className="view"
                        type="button"
                        onClick={() => clickSearch()}
                      >
                        この日の作業を見る
                      </button>
                    )
                  }
                  <button
                    className="close"
                    type="button"
                    onClick={() => closeTip()}
                  >
                    ×
                  </button>
                </div>
              </div>
            </div>
          )
        }
      </>
    </Modal>
  )
}

export default HeatmapModal
